import { generateClient } from 'aws-amplify/data';
import logger from '../utils/logger.js';
import { getCurrentUser } from 'aws-amplify/auth';
import userPreferencesService from './UserPreferencesService.js';
import {
  createNetworkError,
  createValidationError,
  createDataUnavailableError
} from '../utils/StandardErrorFormat.js';
import {
  generateStylePlaceholders as _generateStylePlaceholders,
  applyStyleToContent as _applyStyleToContent,
  applyStyleWithAI as _applyStyleWithAI
} from './_communicationStyle/StyleApplication.js';
import {
  resolveStyle as _resolveStyle,
  getClientStyle as _getClientStyle,
  getTemplateStyle as _getTemplateStyle,
  getDefaultStyle as _getDefaultStyle,
  generateStyleInstructions as _generateStyleInstructions,
  isValidStyle as _isValidStyle,
  getCachedStyle as _getCachedStyle,
  setCachedStyle as _setCachedStyle,
  clearCacheForClient as _clearCacheForClient,
  clearCacheForTemplate as _clearCacheForTemplate,
  clearCacheForUser as _clearCacheForUser,
  clearAllCache as _clearAllCache,
  analyzeWritingSample as _analyzeWritingSample,
  buildStylePrompt as _buildStylePrompt,
  getDefaultStyleSignature as _getDefaultStyleSignature,
  getDefaultStylePrompt as _getDefaultStylePrompt
} from './_communicationStyle/StyleResolver.js';

/**
 * CommunicationStyleService - Handles communication style management
 * Stores and retrieves user's analyzed writing style preferences
 * Supports multiple styles per user for different contexts
 *
 * Merged from:
 * - CommunicationStyleResolver (3-tiered style resolution)
 * - WritingStyleService (AI analysis and prompt building)
 */
class CommunicationStyleService {
  constructor() {
    // Create service-specific logger
    this.logger = logger.child('CommunicationStyleService');

    this.client = generateClient();
    this.cache = new Map();
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
    this.aiService = null; // Will be set later to avoid circular dependency
  }

  // Set the AI service after initialization to avoid circular dependency (from WritingStyleService)
  setAIService(aiService) {
    this.aiService = aiService;
  }

  /**
   * Get current user ID
   * @returns {Promise<string>} User ID
   */
  async getCurrentUserId() {
    try {
      const user = await getCurrentUser();
      return user.userId;
    } catch (error) {
      this.logger.warn('Error getting current user, using mock ID for development:', error);
      // Return mock user ID for development/testing
      return 'test-user-123';
    }
  }

  /**
   * Save communication style to user profile
   * @param {Object} styleAnalysis - Analysis result from NewsletterAnalyzer
   * @returns {Promise<Object>} Updated user profile
   */
  async saveCommunicationStyle(styleAnalysis) {
    try {
      const userId = await this.getCurrentUserId();
      const { tone, style, structure, elements, metadata } = styleAnalysis;

      const communicationStyle = {
        tone: tone.formality,
        formality: tone.formality,
        confidence: tone.confidence,
        personalTouch: tone.personalTouch,
        technicalLevel: style.technicalLanguage,
        sentenceComplexity: style.sentenceComplexity,
        commonPhrases: elements.commonPhrases || [],
        signatureGreeting: elements.signatureElements?.greeting || '',
        signatureClosing: elements.signatureElements?.closing || '',
        firmName: elements.signatureElements?.firmName || metadata.firmName || '',
        analyzedFrom: metadata.originalFile || '',
        lastUpdated: new Date().toISOString()
      };

      // First, try to get existing user profile
      let userProfile;
      try {
        const { data: profiles } = await this.client.models.UserProfile.list({
          filter: { userId: { eq: userId } }
        });
        
        if (profiles && profiles.length > 0) {
          userProfile = profiles[0];
        }
      } catch (error) {
        this.logger.warn('Error fetching user profile:', error);
      }

      if (userProfile) {
        // Update existing profile
        const { data: updatedProfile } = await this.client.models.UserProfile.update({
          id: userProfile.id,
          communicationStyle
        });
        return updatedProfile;
      } else {
        // Create new profile with communication style
        const { data: newProfile } = await this.client.models.UserProfile.create({
          userId,
          firstName: metadata.advisorName?.split(' ')[0] || 'Financial',
          lastName: metadata.advisorName?.split(' ').slice(1).join(' ') || 'Advisor',
          company: metadata.firmName || '',
          communicationStyle
        });
        return newProfile;
      }
    } catch (error) {
      this.logger.error('Error saving communication style:', error);

      if (error.name === 'ServiceError') {
        throw error;
      }

      createNetworkError('Failed to save communication style', error);
    }
  }

  /**
   * Get user's communication style
   * @returns {Promise<Object|null>} Communication style or null
   */
  async getCommunicationStyle() {
    try {
      const userId = await this.getCurrentUserId();
      
      const { data: profiles } = await this.client.models.UserProfile.list({
        filter: { userId: { eq: userId } }
      });

      if (profiles && profiles.length > 0 && profiles[0].communicationStyle) {
        return profiles[0].communicationStyle;
      }

      return null;
    } catch (error) {
      this.logger.error('Error getting communication style:', error);
      return null;
    }
  }

  /**
   * Generate placeholders based on communication style
   * @param {Object} style - Communication style
   * @returns {Array} Array of placeholder suggestions
   */
  generateStylePlaceholders(style) {
    return _generateStylePlaceholders(style);
  }

  /**
   * Get all communication styles for the current user
   * @returns {Promise<Array>} Array of communication styles
   */
  async getAllStyles() {
    try {
      const userId = await this.getCurrentUserId();

      // Try to get from UserPreferences first (for multiple styles)
      const localStyles = await this.getLocalStyles(userId);
      
      // Also try to get from Amplify (legacy single style)
      let amplifyStyle = null;
      try {
        const { data: profiles } = await this.client.models.UserProfile.list({
          filter: { userId: { eq: userId } }
        });
        
        if (profiles && profiles.length > 0 && profiles[0].communicationStyle) {
          amplifyStyle = {
            id: 'amplify-style',
            name: 'Default Style',
            ...profiles[0].communicationStyle,
            source: 'amplify'
          };
        }
      } catch (error) {
        this.logger.warn('Error fetching from Amplify:', error);
      }

      // Combine both sources
      const allStyles = [...localStyles];
      if (amplifyStyle && !allStyles.find(s => s.id === 'amplify-style')) {
        allStyles.push(amplifyStyle);
      }

      return allStyles;
    } catch (error) {
      this.logger.error('Error getting all styles:', error);
      return [];
    }
  }

  /**
   * Create a new communication style
   * @param {Object} styleData - Style data
   * @returns {Promise<Object>} Created style
   */
  async createStyle(styleData) {
    try {
      const userId = await this.getCurrentUserId();
      const newStyle = {
        id: `style-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        userId,
        ...styleData,
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      };

      // Save to UserPreferences
      const existingStyles = await this.getLocalStyles(userId);
      existingStyles.push(newStyle);
      await this.saveLocalStyles(userId, existingStyles);

      return newStyle;
    } catch (error) {
      this.logger.error('Error creating style:', error);

      if (error.name === 'ServiceError') {
        throw error;
      }

      throw createNetworkError('Failed to create communication style', error);
    }
  }

  /**
   * Create a communication style from analysis result
   * @param {Object} analysisResult - Analysis result from NewsletterAnalyzer
   * @returns {Promise<Object>} Created style
   */
  async createStyleFromAnalysis(analysisResult) {
    const { tone, style, structure, elements, metadata } = analysisResult;
    
    const styleData = {
      name: metadata.analysisName || `Style from ${metadata.originalFile || 'Analysis'}`,
      formality: tone.formality,
      confidence: tone.confidence,
      personalTouch: tone.personalTouch,
      technicalLevel: style.technicalLanguage,
      sentenceComplexity: style.sentenceComplexity,
      commonPhrases: elements.commonPhrases || [],
      signatureGreeting: elements.signatureElements?.greeting || '',
      signatureClosing: elements.signatureElements?.closing || '',
      firmName: elements.signatureElements?.firmName || metadata.firmName || '',
      analyzedFrom: metadata.originalFile || '',
      source: 'analysis'
    };

    return this.createStyle(styleData);
  }

  /**
   * Update an existing communication style
   * @param {string} styleId - Style ID
   * @param {Object} updateData - Updated style data
   * @returns {Promise<Object>} Updated style
   */
  async updateStyle(styleId, updateData) {
    try {
      const userId = await this.getCurrentUserId();
      
      if (styleId === 'amplify-style') {
        // Update the Amplify profile style
        const { data: profiles } = await this.client.models.UserProfile.list({
          filter: { userId: { eq: userId } }
        });
        
        if (profiles && profiles.length > 0) {
          const communicationStyle = {
            tone: updateData.formality,
            formality: updateData.formality,
            confidence: updateData.confidence,
            personalTouch: updateData.personalTouch,
            technicalLevel: updateData.technicalLevel,
            sentenceComplexity: updateData.sentenceComplexity,
            commonPhrases: updateData.commonPhrases || [],
            signatureGreeting: updateData.signatureGreeting || '',
            signatureClosing: updateData.signatureClosing || '',
            firmName: updateData.firmName || '',
            analyzedFrom: updateData.analyzedFrom || '',
            lastUpdated: new Date().toISOString()
          };

          await this.client.models.UserProfile.update({
            id: profiles[0].id,
            communicationStyle
          });

          return {
            id: 'amplify-style',
            name: updateData.name || 'Default Style',
            ...communicationStyle,
            source: 'amplify'
          };
        }
      } else {
        // Update UserPreferences style
        const existingStyles = await this.getLocalStyles(userId);
        const styleIndex = existingStyles.findIndex(s => s.id === styleId);

        if (styleIndex !== -1) {
          existingStyles[styleIndex] = {
            ...existingStyles[styleIndex],
            ...updateData,
            lastUpdated: new Date().toISOString()
          };

          await this.saveLocalStyles(userId, existingStyles);
          return existingStyles[styleIndex];
        }
      }

      throw createDataUnavailableError('Style not found');
    } catch (error) {
      this.logger.error('Error updating style:', error);

      if (error.name === 'ServiceError') {
        throw error;
      }

      throw createNetworkError('Failed to update communication style', error);
    }
  }

  /**
   * Delete a communication style
   * @param {string} styleId - Style ID
   * @returns {Promise<void>}
   */
  async deleteStyle(styleId) {
    try {
      const userId = await this.getCurrentUserId();

      if (styleId === 'amplify-style') {
        throw createValidationError('Cannot delete the default Amplify style');
      }

      const existingStyles = await this.getLocalStyles(userId);
      const filteredStyles = existingStyles.filter(s => s.id !== styleId);

      if (filteredStyles.length === existingStyles.length) {
        throw createDataUnavailableError('Style not found');
      }

      await this.saveLocalStyles(userId, filteredStyles);
    } catch (error) {
      this.logger.error('Error deleting style:', error);

      if (error.name === 'ServiceError') {
        throw error;
      }

      throw createNetworkError('Failed to delete communication style', error);
    }
  }

  /**
   * Get a specific communication style by ID
   * @param {string} styleId - Style ID
   * @returns {Promise<Object|null>} Communication style or null
   */
  async getStyleById(styleId) {
    try {
      const allStyles = await this.getAllStyles();
      return allStyles.find(style => style.id === styleId) || null;
    } catch (error) {
      this.logger.error('Error getting style by ID:', error);
      return null;
    }
  }

  /**
   * Get communication styles from UserPreferences
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of styles
   */
  async getLocalStyles(userId) {
    try {
      const prefs = await userPreferencesService.getPreferences();
      const aiSettings = prefs.aiSettings || {};
      return aiSettings.communicationStyles || [];
    } catch (error) {
      this.logger.error('Error getting communication styles:', error);
      return [];
    }
  }

  /**
   * Save communication styles to UserPreferences
   * @param {string} userId - User ID
   * @param {Array} styles - Array of styles
   */
  async saveLocalStyles(userId, styles) {
    try {
      await userPreferencesService.updateAISettings({
        communicationStyles: styles
      });
    } catch (error) {
      this.logger.error('Error saving communication styles:', error);
      throw error;  // Re-throw to propagate error to caller
    }
  }

  /**
   * Combine multiple communication styles
   * @param {Array} styleIds - Array of style IDs to combine
   * @param {Object} options - Combination options
   * @returns {Promise<Object>} Combined style
   */
  async combineStyles(styleIds, options = {}) {
    try {
      const styles = await Promise.all(
        styleIds.map(id => this.getStyleById(id))
      );

      const validStyles = styles.filter(s => s !== null);
      if (validStyles.length === 0) {
        throw createDataUnavailableError('No valid styles found');
      }

      // Combine styles with weighted averaging or priority-based selection
      const combinedStyle = {
        name: options.name || `Combined Style (${validStyles.length} styles)`,
        formality: this.combineProperty(validStyles, 'formality', options.priority),
        confidence: this.combineProperty(validStyles, 'confidence', options.priority),
        personalTouch: this.combineProperty(validStyles, 'personalTouch', options.priority),
        technicalLevel: this.combineProperty(validStyles, 'technicalLevel', options.priority),
        sentenceComplexity: this.combineProperty(validStyles, 'sentenceComplexity', options.priority),
        commonPhrases: this.combineArrays(validStyles, 'commonPhrases'),
        signatureGreeting: validStyles[0].signatureGreeting || '',
        signatureClosing: validStyles[0].signatureClosing || '',
        firmName: validStyles[0].firmName || '',
        source: 'combined',
        combinedFrom: validStyles.map(s => s.name)
      };

      return combinedStyle;
    } catch (error) {
      this.logger.error('Error combining styles:', error);

      if (error.name === 'ServiceError') {
        throw error;
      }

      throw createNetworkError('Failed to combine communication styles', error);
    }
  }

  /**
   * Combine a property from multiple styles
   * @param {Array} styles - Array of styles
   * @param {string} property - Property to combine
   * @param {string} priority - Priority style ID
   * @returns {*} Combined property value
   */
  combineProperty(styles, property, priority = null) {
    if (priority) {
      const priorityStyle = styles.find(s => s.id === priority);
      if (priorityStyle && priorityStyle[property]) {
        return priorityStyle[property];
      }
    }

    // Use the most common value
    const values = styles.map(s => s[property]).filter(v => v);
    if (values.length === 0) {
      return undefined;  // No styles have this property
    }

    const counts = {};
    values.forEach(v => counts[v] = (counts[v] || 0) + 1);

    return Object.keys(counts).reduce((a, b) => counts[a] > counts[b] ? a : b);
  }

  /**
   * Combine arrays from multiple styles
   * @param {Array} styles - Array of styles
   * @param {string} property - Array property to combine
   * @returns {Array} Combined array
   */
  combineArrays(styles, property) {
    const combined = [];
    styles.forEach(style => {
      if (style[property] && Array.isArray(style[property])) {
        combined.push(...style[property]);
      }
    });
    return [...new Set(combined)]; // Remove duplicates
  }

  /**
   * Apply communication style to content with enhanced transformations
   * @param {string} content - Original content
   * @param {Object} style - Communication style object
   * @param {Object} options - Optional settings { returnMetadata: boolean }
   * @returns {string|Object} Styled content or { content, transformations } if returnMetadata=true
   */
  applyStyleToContent(content, style, options = {}) {
    return _applyStyleToContent(content, style, options);
  }

  /**
   * Apply style using AI regeneration (comprehensive transformation)
   * @param {string} content - Original content
   * @param {Object} styleProfile - Style profile object
   * @param {Object} options - Optional settings
   * @returns {Promise<Object>} { content, tokensUsed, estimatedCost }
   */
  async applyStyleWithAI(content, styleProfile, options = {}) {
    return _applyStyleWithAI(this.aiService, content, styleProfile, options);
  }

  // ========================================
  // Methods merged from CommunicationStyleResolver
  // (delegated to ./_communicationStyle/StyleResolver.js)
  // ========================================

  /**
   * Internal cache state passed to StyleResolver helpers (per-instance, not
   * shared between services so concurrent users don't share their cache).
   */
  _cacheState() {
    return { cache: this.cache, cacheExpiry: this.cacheExpiry };
  }

  /**
   * Main style resolution method - returns the first complete style found
   * @param {Object} options
   * @param {string} options.clientId - Client ID for client-specific style
   * @param {string} options.templateId - Template ID for template-specific style
   * @param {string} options.userId - User ID for default style
   * @returns {Object} Complete communication style with source information (not merged)
   */
  async resolveStyle(opts) {
    return _resolveStyle(this._cacheState(), this.client, this.logger, opts);
  }

  async getClientStyle(clientId) {
    return _getClientStyle(this.client, this.logger, clientId);
  }

  async getTemplateStyle(templateId) {
    return _getTemplateStyle(this.client, this.logger, templateId);
  }

  async getDefaultStyle(userId) {
    return _getDefaultStyle(this.client, this.logger, userId);
  }

  async saveStyleAtLevel(styleData, { level, userId }) {
    if (level !== 'default') {
      throw new Error(`saveStyleAtLevel: unsupported level "${level}"`);
    }
    // Review fix: guard null so we fail fast instead of dereferencing
    // styleData.tone mid-flow (after a wasted round-trip).
    if (!styleData || typeof styleData !== 'object') {
      throw new Error('saveStyleAtLevel: styleData is required');
    }

    // Review fix: reuse the typed models.UserProfile flow (same as
    // saveCommunicationStyle) rather than hand-written raw GraphQL — consistent
    // client/auth, and a list glitch throws instead of silently creating a
    // duplicate profile.
    const { data: profiles } = await this.client.models.UserProfile.list({
      filter: { userId: { eq: userId } },
    });
    const existing = (profiles || [])[0];

    // Only the fields this manager edits. Spread over the EXISTING style so
    // fields it doesn't own (e.g. firmName from the writing-sample analyzer)
    // survive instead of being wiped.
    const styleFields = {
      tone: styleData.tone,
      formality: styleData.formality,
      confidence: styleData.confidence,
      personalTouch: styleData.personalTouch,
      technicalLevel: styleData.technicalLevel,
      sentenceComplexity: styleData.sentenceComplexity,
      commonPhrases: styleData.commonPhrases || [],
      signatureGreeting: styleData.signatureGreeting || '',
      signatureClosing: styleData.signatureClosing || '',
      analyzedFrom: styleData.analyzedFrom || '',
      lastUpdated: new Date().toISOString(),
    };

    if (existing) {
      const communicationStyle = { ...(existing.communicationStyle || {}), ...styleFields };
      await this.client.models.UserProfile.update({ id: existing.id, communicationStyle });
    } else {
      // Create requires non-null firstName/lastName — mirror
      // saveCommunicationStyle's placeholder defaults.
      await this.client.models.UserProfile.create({
        userId,
        firstName: 'Financial',
        lastName: 'Advisor',
        communicationStyle: styleFields,
      });
    }
    this.logger.info('Saved default communication style for user:', userId);
  }

  generateStyleInstructions(style) {
    return _generateStyleInstructions(style);
  }

  isValidStyle(style) {
    return _isValidStyle(style);
  }

  // Cache management — operates on the per-instance Map owned by the facade
  getCachedStyle(key) {
    return _getCachedStyle(this._cacheState(), key);
  }

  setCachedStyle(key, style) {
    return _setCachedStyle(this._cacheState(), key, style);
  }

  clearCacheForClient(clientId) {
    return _clearCacheForClient(this._cacheState(), clientId);
  }

  clearCacheForTemplate(templateId) {
    return _clearCacheForTemplate(this._cacheState(), templateId);
  }

  clearCacheForUser(userId) {
    return _clearCacheForUser(this._cacheState(), userId);
  }

  clearAllCache() {
    return _clearAllCache(this._cacheState());
  }

  // ========================================
  // Methods merged from WritingStyleService
  // (delegated to ./_communicationStyle/StyleResolver.js)
  // ========================================

  async analyzeWritingSample(sample, aiService = null) {
    // #174 follow-up: lazy-wire the AI service if a caller hasn't done so
    // already. Previously, analyzers invoked from Settings → AI (which
    // never calls setAIService) silently fell back to the default
    // signature because this.aiService was null. Dynamic import avoids
    // the circular dependency that originally motivated the manual
    // setAIService pattern.
    let resolvedAIService = aiService || this.aiService;
    if (!resolvedAIService) {
      try {
        const mod = await import('./NewsletterAIService.js');
        resolvedAIService = mod.default;
        this.aiService = resolvedAIService; // memoize for subsequent calls
      } catch (err) {
        this.logger.error('Failed to lazy-load NewsletterAIService for style analysis', err);
      }
    }
    return _analyzeWritingSample(resolvedAIService, this.logger, sample, aiService);
  }

  buildStylePrompt(preferences) {
    return _buildStylePrompt(preferences);
  }

  _getDefaultStyleSignature() {
    return _getDefaultStyleSignature();
  }

  _getDefaultStylePrompt() {
    return _getDefaultStylePrompt();
  }
}

// Create singleton instance
const communicationStyleServiceInstance = new CommunicationStyleService();

// Check feature flag for singleton behavior
const USE_SINGLETON = import.meta.env?.VITE_USE_COMMUNICATION_STYLE_SINGLETON !== 'false' &&
                      process.env.REACT_APP_USE_COMMUNICATION_STYLE_SINGLETON !== 'false';

// Export based on feature flag
export default USE_SINGLETON ? communicationStyleServiceInstance : CommunicationStyleService;

// Always export the class for testing
export { CommunicationStyleService };

// ═════════════════════════════════════════════════════════════════════════
// StyleProfileService re-exported from internal module
// (originally merged into this file in Sprint 3 #51, extracted to its own
// module in Sprint 4 #54 to keep this file under the 800-LOC budget).
// Public API preserved verbatim — `new StyleProfileService()` and the
// default singleton `styleProfileServiceInstance` continue to work.
// ═════════════════════════════════════════════════════════════════════════

export { StyleProfileService, styleProfileServiceInstance } from './_communicationStyle/StyleProfiles.js';

// ═════════════════════════════════════════════════════════════════════════
// StyleLearningService re-exported from internal module
// (originally merged into this file in Sprint 3 #51, extracted to its own
// module in Sprint 4 #54 to keep this file under the 800-LOC budget).
// Public API preserved verbatim — `new StyleLearningService()` and the
// default singleton `styleLearningServiceInstance` continue to work.
// ═════════════════════════════════════════════════════════════════════════

export { StyleLearningService, styleLearningServiceInstance } from './_communicationStyle/StyleLearning.js';
